import React, { memo, useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Typography,
} from "@mui/material";
import zxcvbn from "zxcvbn";
import { useTranslation } from "react-i18next";
import { TickGreenIcon } from "../../styles/img/svg/TickGreenIcon";
import { TickGreyIcon } from "../../styles/img/svg/TickGreyIcon";
import { VisibilityIcon } from "../../styles/img/svg/VisibilityIcon";
import { VisibilityIconOff } from "../../styles/img/svg/VisibilityIconOff";

const UiInput = memo((props) => {
  const { t, i18n } = useTranslation();
  const {
    focus,
    label,
    value,
    name,
    type,
    error,
    placeholder,
    multiline,
    maxRows,
    minRows,
    onInput,
    onChange,
    readonly,
    helperText,
    clearable,
    inputStartImg,
    inputIcon,
    inputonChange,
    startadornmenttext,
    endadornmenttext,
    inputStartIcon,
    focusOut,
    onBlur,
    onMouseLeave,
    maxLength,
    minLength,
    required,
    resetValidation,
    endAdornmentOnClick,
    "data-testid": dataTestId,
    ...restProps
  } = props;

  const shouldMarkTouchedOnBlur = !!props.showErrorOutFous;

  const inputRef = useRef();

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (focus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focus]);

  const renderEndAdornment = () => {
    if (!endadornmenttext && !inputIcon) {
      return undefined;
    }

    return (
      <InputAdornment position="end">
        {endadornmenttext && (
          <span className="df-input-end-adornment-text">{endadornmenttext}</span>
        )}
        {inputIcon && (
          <IconButton
            onClick={endAdornmentOnClick}
            aria-label="Edit input"
            edge="end"
            tabIndex={-1}
          >
            {inputIcon}
          </IconButton>
        )}
      </InputAdornment>
    );
  };

  return (
    <>
      <Field name={name}>
        {({ input, meta }) => (
          <FormControl
            sx={{ mb: 2 }}
            variant="outlined"
            fullWidth
            error={Boolean(error) || (meta.touched && Boolean(meta.error))}
          >
            <InputLabel htmlFor={name}>
              {label} {required && "*"}
            </InputLabel>
            {type == "password" ? (
              <OutlinedInput
                {...restProps}
                {...input}
                type={showPassword ? "text" : "password"}
                className="df-input-field"
                inputProps={{
                  maxLength: maxLength,
                  minLength: minLength,
                }}
                onInput={(e) => {
                  resetValidation != undefined && resetValidation();
                }}
                readOnly={readonly}
                {...(inputonChange ? { onChange: inputonChange } : {})}
                endAdornment={
                  !props.disableAdornment && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityIconOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  )
                }
                label={label}
                onBlur={(e) => {
                  // call final-form blur
                  input.onBlur && input.onBlur(e);
                  // call provided onBlur prop or legacy focusOut
                  if (onBlur) onBlur(e);
                  else if (focusOut) focusOut(e.target?.value);
                }}
                onMouseLeave={(e) => {
                  if (onMouseLeave) onMouseLeave(e);
                  else if (restProps.onMouseLeave) restProps.onMouseLeave(e);
                }}
              />
            ) : inputStartIcon || startadornmenttext || inputStartImg ? (
              <OutlinedInput
                {...restProps}
                {...input}
                type={type}
                inputRef={inputRef}
                placeholder={placeholder}
                multiline={multiline}
                maxRows={maxRows}
                minRows={minRows}
                inputProps={{
                  maxLength: maxLength,
                  minLength: minLength,
                  accept: props?.accept,
                  "data-testid": dataTestId,
                }}
                label={label}
                value={value ?? input.value}
                onBlur={(event) => {
                  input.onBlur && input.onBlur(event);
                  if (onBlur) onBlur(event);
                  else if (focusOut) focusOut(event.target?.value);
                }}
                onMouseLeave={(e) => {
                  if (onMouseLeave) onMouseLeave(e);
                  else if (restProps.onMouseLeave) restProps.onMouseLeave(e);
                }}
                className="df-input-field"
                readOnly={readonly}
                {...(inputonChange ? { onChange: inputonChange } : {})}
                startAdornment={
                  <InputAdornment
                    position="start"
                    className="df-inp-start-icon"
                  >
                    <IconButton aria-label="Edit input" edge="end">
                      {inputStartIcon && inputStartIcon}
                      {startadornmenttext && startadornmenttext}
                      {inputStartImg && <img src={inputStartImg} />}
                    </IconButton>
                  </InputAdornment>
                }
                endAdornment={renderEndAdornment()}
              />
            ) : (
              <OutlinedInput
                inputRef={inputRef}
                {...restProps}
                {...input}
                type={type}
                placeholder={placeholder}
                multiline={multiline}
                maxRows={maxRows}
                minRows={minRows}
                inputProps={{
                  maxLength: maxLength,
                  minLength: minLength,
                  accept: props?.accept,
                  "data-testid": dataTestId,
                }}
                label={label}
                value={value ?? input.value}
                onBlur={(event) => {
                  if (shouldMarkTouchedOnBlur && input.onBlur) {
                    input.onBlur(event);
                  }

                  if (onBlur) {
                    onBlur(event);
                  }

                  if (focusOut) {
                    focusOut(event.target?.value);
                  }
                }}
                onMouseLeave={(e) => {
                  if (onMouseLeave) onMouseLeave(e);
                  else if (restProps.onMouseLeave) restProps.onMouseLeave(e);
                }}
                className="df-input-field"
                readOnly={readonly}
                {...(inputonChange ? { onChange: inputonChange } : {})}
                endAdornment={renderEndAdornment()}
              />
            )}
            {(error || (meta.touched && meta.error)) && (
              <FormHelperText className="component-error-text">
                {error || meta.error}
              </FormHelperText>
            )}

            {props.helperText && (
              <FormHelperText className="df-helper-text">
                {props.helperText}
              </FormHelperText>
            )}
          </FormControl>
        )}
      </Field>
    </>
  );
});

const UiInputPassword = memo((props) => {
  const {
    label,
    value,
    name,
    type,
    error,
    placeholder,
    startadornmenttext,
    inputStartImg,
    multiline,
    maxRows,
    minRows,
    onInput,
    inputStartIcon,
    inputonChange,
    readOnly,
    helperText,
    customInputProps,
    inputIcon,
    accept,
    enableStrength,
    enableConditions,
    required,
    maxLength,
  } = props;

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const { t, i18n } = useTranslation();
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const strength = value ? zxcvbn(value).score : 0;

  //to matches with new password strenght meter doc , Repeated "Average" labels are intentional to map multiple intermediate scores to the same label.
  const getColor = (score) => {
    switch (score) {
      case 0:
        return "error";
      case 1:
      case 2:
      case 3:
        return "info";
      case 4:
        return "success";
      default:
        return "error";
    }
  };

  const getStrengthLabel = (score) => {
    const labels = [
      t("Weak"),
      t("Average"),
      t("Average"),
      t("Average"),
      t("Strong"),
    ];
    return labels[score];
  };

  return (
    <>
      <FormControl
        sx={{ mb: 2 }}
        variant="outlined"
        fullWidth
        error={error}
        required={required}
      >
        <InputLabel htmlFor={name}>{label}</InputLabel>
        {type == "password" ? (
          <>
            <OutlinedInput
              {...props}
              type={showPassword ? "text" : "password"}
              className="df-input-field"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityIconOff /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              }
              inputProps={{ maxLength: maxLength }}
              label={label}
            />

            {enableStrength && (
              <>
                <div className="df-pw-strength-progress">
                  <LinearProgress
                    variant="determinate"
                    value={(strength + 1) * 20}
                    color={getColor(strength)}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    {getStrengthLabel(strength)}
                  </Typography>
                </div>
              </>
            )}

            {enableConditions && (
              <div className="df-pw-conditions">
                <div className="df-pw-conditions__item">
                  <TickGreenIcon />{" "}
                  <p>{t("Password length should be at least 8 characters.")}</p>
                </div>

                <div className="df-pw-conditions__item">
                  <TickGreenIcon />{" "}
                  <p>
                    {t(
                      "Password should have at least 1 uppercase letter, 1 lowercase, 1 numeric character and 1 special character (e.g. !#$%^&*()+@=)",
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {inputStartIcon || startadornmenttext || inputStartImg ? (
              <OutlinedInput
                {...props}
                type={type}
                label={label}
                placeholder={placeholder}
                multiline={multiline}
                maxRows={maxRows}
                minRows={minRows}
                accept={props?.accept}
                className="df-input-field"
                onChange={inputonChange}
                inputProps={{
                  ...customInputProps,
                  maxLength: maxLength,
                }}
                startAdornment={
                  <InputAdornment
                    position="start"
                    className="df-inp-start-icon"
                  >
                    <IconButton aria-label="Edit input" edge="end">
                      {inputStartIcon && inputStartIcon}
                      {startadornmenttext && startadornmenttext}
                      {inputStartImg && <img src={inputStartImg} />}
                    </IconButton>
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={endAdornmentOnClick}
                      aria-label="Edit input"
                      edge="end"
                      tabIndex={-1}
                    >
                      {inputIcon && inputIcon}
                    </IconButton>
                  </InputAdornment>
                }
              />
            ) : (
              <OutlinedInput
                {...props}
                type={type}
                label={label}
                placeholder={placeholder}
                multiline={multiline}
                maxRows={maxRows}
                minRows={minRows}
                accept={props?.accept}
                className="df-input-field"
                onChange={inputonChange}
                inputProps={{
                  ...customInputProps,
                  maxLength: maxLength,
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={endAdornmentOnClick}
                      aria-label="Edit input"
                      edge="end"
                      tabIndex={-1}
                    >
                      {inputIcon && inputIcon}
                    </IconButton>
                  </InputAdornment>
                }
              />
            )}
          </>
        )}
        {error && (
          <FormHelperText className="component-error-text">
            {error}
          </FormHelperText>
        )}
        {props.helperText && (
          <FormHelperText className="df-helper-text">
            {props.helperText}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
});

const UiInputPasswordWithFinalForm = memo((props) => {
  const {
    label,
    name,
    type = "password",
    placeholder,
    startadornmenttext,
    inputStartImg,
    multiline,
    maxRows,
    minRows,
    inputStartIcon,
    inputIcon,
    accept,
    enableStrength,
    enableConditions,
    required,
    helperText,
    maxLength,
    minLength = 8,
    customInputProps,
    passwordValidatePolicy,
    specialCharactersCheck,
    passwordValue,
    disableCopyPaste = false,
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (e) => e.preventDefault();
  const preventClipboard = (e) => e.preventDefault();
  const { t } = useTranslation();

  const getColor = (score) => {
    if (score >= 5) return "#48CE96";
    if (score === 4) return "#FFD600";
    return "#CF6679";
  };

  function getStrengthLabel(score) {
    if (score >= 5) return t("Strong");
    if (score === 4) return t("Average");
    return t("Weak");
  }

  function getCustomPasswordScore(password) {
    let score = 0;
    if (!password) return 0;

    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!#$%^&*()+@=]/.test(password)) score += 1;
    if (password.length >= 12) score += 1;

    return score;
  }

  return (
    <Field name={name}>
      {({ input, meta }) => {
        const value = input.value || "";
        const strength = getCustomPasswordScore(value);

        const hasStartAdornment =
          inputStartIcon || startadornmenttext || inputStartImg;

        const color = getColor(strength);

        const isPaletteColor = [
          "error",
          "success",
          "primary",
          "secondary",
          "warning",
          "info",
        ].includes(color);

        return (
          <FormControl
            sx={{ mb: 2 }}
            variant="outlined"
            fullWidth
            error={Boolean(meta.touched && meta.error)}
            required={required}
          >
            <InputLabel htmlFor={name}>{label}</InputLabel>

            <OutlinedInput
              {...input}
              id={name}
              value={passwordValue}
              type={showPassword ? "text" : "password"}
              placeholder={placeholder}
              multiline={multiline}
              maxRows={maxRows}
              minRows={minRows}
              label={label}
              className="df-input-field"
              inputProps={{
                ...customInputProps,
                maxLength,
                accept,
              }}
              {...(disableCopyPaste && {
                onCopy: preventClipboard,
                onPaste: preventClipboard,
                onCut: preventClipboard,
              })}
              startAdornment={
                hasStartAdornment && (
                  <InputAdornment
                    position="start"
                    className="df-inp-start-icon"
                  >
                    <IconButton edge="start">
                      {inputStartIcon}
                      {startadornmenttext}
                      {inputStartImg && (
                        <img src={inputStartImg} alt="start-icon" />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onMouseDown={handleMouseDownPassword}
                    onClick={handleClickShowPassword}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityIconOff /> : <VisibilityIcon />}
                  </IconButton>
                  {inputIcon && (
                    <IconButton
                      onClick={endAdornmentOnClick}
                      edge="end"
                      aria-label="input-icon"
                    >
                      {inputIcon}
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />

            {meta.touched && meta.error && (
              <FormHelperText className="component-error-text">
                {meta.error}
              </FormHelperText>
            )}

            {helperText && (
              <FormHelperText className="df-helper-text">
                {helperText}
              </FormHelperText>
            )}

            {enableStrength && value ? (
              <div className="df-pw-strength-progress">
                <LinearProgress
                  variant="determinate"
                  value={(strength / 5) * 100}
                  color={isPaletteColor ? color : "inherit"}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: "#eee", // track
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: isPaletteColor ? undefined : color, // hex override
                    },
                  }}
                />
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  {getStrengthLabel(strength)}
                </Typography>
              </div>
            ) : (
              <>
                <br />
                <br />
              </>
            )}

            {enableConditions && (
              <div className="df-pw-conditions">
                <div className="df-pw-conditions__item">
                  {value.length >= minLength ? (
                    <TickGreenIcon />
                  ) : (
                    <TickGreyIcon />
                  )}
                  <p>{t("Password length should be at least 8 characters.")}</p>
                </div>
                <div className="df-pw-conditions__item">
                  {specialCharactersCheck?.(value) ? (
                    <TickGreenIcon />
                  ) : (
                    <TickGreyIcon />
                  )}
                  <p>
                    {t(
                      "Password should have at least 1 uppercase letter, 1 lowercase, 1 numeric character and 1 special character (e.g. !#$%^&*()+@=)",
                    )}
                  </p>
                </div>
              </div>
            )}
          </FormControl>
        );
      }}
    </Field>
  );
});

export default UiInput;
export { UiInputPassword, UiInputPasswordWithFinalForm };
